import { ANCHOR_WATCH_COMMAND_ACTIONS, type AnchorWatchCommandAction } from '@neomaris/protocol';
import { useEffect, useRef, useState } from 'react';

import { nextDisplayAt, retimeBatch } from '../components/charts/lib/motion';
import { LiveSampleStore } from './liveSampleStore';
import {
  foldFrame,
  observeClockOffset,
  sampledAt,
  type ClockOffsetObservation,
} from './sampleClock';
import { beforeWsReconnect } from './wsReconnect';
import { BOAT_NAMES_CHANGED_EVENT } from './useBoatNames';
import { parseLooksChangedFrame } from './boardLooks';

// React hook that opens a single user-WS at `/v1/ws`, subscribes to
// `boat.<id>.data` and `boat.<id>.info` for every id in `boatIds`, accumulates
// the latest value seen for each (boatId, path) pair, and exposes aggregate
// invalidation revisions on that same connection.
//
// Receive shape per user-ws.ts:136 →
//   { type: 'data', topic: 'boat.<uuid>.data', ts: number,
//     payload: Array<Sample> }
// where Sample mirrors the Zod shape in cloud/src/core/telemetry/ingest.ts
// after JSON serialization (Date → ISO string).
//
// Auth: cookie. The browser includes `nc_access` on the WebSocket upgrade
// because the page origin matches the WS origin.

export interface LiveSample {
  time: string;
  path: string;
  value: number | string | boolean | Record<string, unknown> | undefined;
  unit?: string;
  source?: string;
  deviceId?: string;
  receivedAt: number;
  /** `time` re-expressed on THIS browser's clock, preserving in-frame spacing
   *  (`retimeBatch`). `receivedAt` is the frame's arrival instant and is the
   *  same for every sample in a batch; this differs per sample, which is what
   *  makes interpolation possible. */
  displayAt?: number;
  /** When the sample was taken, on this browser's clock: its own `time` plus
   *  the boat's measured clock offset, never after `receivedAt`. A reading's
   *  age and freshness run from this, so a sample batched a minute late reads a
   *  minute old (`sampleClock.ts`, TOM-441). */
  sampledAt?: number;
}

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export interface BoatLiveData {
  status: ConnectionStatus;
  // Map<boatId, Map<path, LiveSample>>. Returned as a frozen reference
  // per state update so consumers can `useMemo` on it.
  latest: ReadonlyMap<string, ReadonlyMap<string, LiveSample>>;
  lastSampleAt: ReadonlyMap<string, number>;
  /** Incremented when cloud publishes a boat-information invalidation. */
  infoRevision: ReadonlyMap<string, number>;
  /** Latest anchor-command status beat per boat (cmdAck lane, ADR 0135). */
  anchorCommandStatus: ReadonlyMap<string, AnchorCommandStatusEvent>;
  /** The newest `board_changed` info frame per boat (cloud ADR 0291 §8, TOM-192). */
  boardChanged: ReadonlyMap<string, BoardChangedEvent>;
  /**
   * Incremented on each `equipment_changed` info frame per boat: an owner, on
   * any device, answered about the boat's equipment (cloud ADR 0312 §7,
   * TOM-409). A page that shows the equipment reads it again.
   */
  equipmentRevision: ReadonlyMap<string, number>;
  namesRevision: ReadonlyMap<string, number>;
  /**
   * The newest `looks` frame per boat: the signed-in owner's own panel looks
   * for that boat changed, on any of their screens (TOM-595, cloud ADR 0364
   * §7). An open board reads them again.
   */
  looksChanged: ReadonlyMap<string, LooksChangedEvent>;
  /** The per-path live lane. STABLE identity for the hook's lifetime, so it
   *  can be threaded through context without invalidating memos. */
  liveStore: LiveSampleStore;
}

/**
 * A board on the boat changed, from any path: an owner's edit, Neo's, an undo
 * (`NeoBoardChangedFrame`). It carries no items: a client showing the boards
 * refetches the list, and the open board when `slug` matches.
 */
export interface BoardChangedEvent {
  slug: string;
  /** The board's version now; null when it was removed. */
  version: number | null;
  /** The version of the boat's order of boards now. */
  orderVersion: number;
  /** Counts frames, so two frames with the same versions still read as two changes. */
  seq: number;
}

/** The owner's own looks for a boat are now at `revision` (`looks` frame, TOM-592). */
export interface LooksChangedEvent {
  revision: number;
  /** Counts frames, so the same revision sent twice still reads as two changes. */
  seq: number;
}

export interface AnchorCommandStatusEvent {
  commandKey: string;
  action: AnchorWatchCommandAction;
  phase: 'accepted' | 'complete' | 'rejected';
  detail?: string;
  receivedAt: number;
}

interface IncomingFrame {
  type?: string;
  topic?: string;
  ts?: number;
  payload?: unknown;
}

interface IncomingSample {
  time?: unknown;
  path?: unknown;
  value?: unknown;
  unit?: unknown;
  source?: unknown;
  deviceId?: unknown;
}

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 16_000;

/**
 * Floor on how often the cloned-Map snapshot is republished.
 *
 * The snapshot is a whole-page dependency: changing it re-renders every
 * consumer of `latest`. Before the fast lane that was fine, because samples
 * arrived seconds apart, and stops being fine as the fast tier speeds up.
 * Instruments read the per-path store instead and are unaffected by this
 * throttle; everything else was already happy at ≤1 Hz.
 *
 * Status, info and cmdAck publishes are deliberately NOT throttled — they are
 * rare and latency matters for them.
 */
const SNAPSHOT_MIN_INTERVAL_MS = 1_000;

function dataTopicForBoat(boatId: string): string {
  return `boat.${boatId}.data`;
}

/** Whether an info frame's payload is `equipment_changed` (cloud ADR 0312 §7). */
export function isEquipmentChangedFrame(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'equipment_changed'
  );
}

/**
 * The `latest` a snapshot publishes: a new map for every boat as well as the
 * outer one. The working maps are written in place frame by frame, so a boat's
 * map handed out as it is keeps one identity for the hook's life, and anything
 * memoised on `latest.get(boatId)` never sees a new sample. The header's quick
 * readings froze on their first values that way (TOM-412).
 */
export function snapshotLatest(
  working: ReadonlyMap<string, ReadonlyMap<string, LiveSample>>
): Map<string, Map<string, LiveSample>> {
  return new Map([...working].map(([boatId, samples]) => [boatId, new Map(samples)]));
}

export function isNamesChangedFrame(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'names_changed'
  );
}

/** A `board_changed` info frame's payload, or null when the frame is another kind. */
export function parseBoardChangedFrame(payload: unknown): Omit<BoardChangedEvent, 'seq'> | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const p = payload as {
    kind?: unknown;
    slug?: unknown;
    version?: unknown;
    orderVersion?: unknown;
  };
  if (p.kind !== 'board_changed' || typeof p.slug !== 'string' || p.slug === '') return null;
  if (typeof p.orderVersion !== 'number') return null;
  const version = typeof p.version === 'number' ? p.version : null;
  return { slug: p.slug, version, orderVersion: p.orderVersion };
}

function infoTopicForBoat(boatId: string): string {
  return `boat.${boatId}.info`;
}

function cmdAckTopicForBoat(boatId: string): string {
  return `boat.${boatId}.cmdAck`;
}

function extractBoatId(topic: string): string | null {
  const m = /^boat\.([0-9a-f-]{36})\.data$/i.exec(topic);
  return m === null ? null : m[1]!;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Off the shared list, so a new action reaches this lane with the protocol. */
function isAnchorAction(v: unknown): v is AnchorWatchCommandAction {
  return (ANCHOR_WATCH_COMMAND_ACTIONS as readonly unknown[]).includes(v);
}

function parseSample(raw: unknown, now: number): LiveSample | null {
  if (!isPlainObject(raw)) return null;
  const s = raw as IncomingSample;
  if (typeof s.path !== 'string' || s.path.length === 0) return null;
  if (typeof s.time !== 'string') return null;
  const out: LiveSample = {
    time: s.time,
    path: s.path,
    value: s.value as LiveSample['value'],
    receivedAt: now,
  };
  if (typeof s.unit === 'string') out.unit = s.unit;
  if (typeof s.source === 'string') out.source = s.source;
  if (typeof s.deviceId === 'string') out.deviceId = s.deviceId;
  return out;
}

export interface UseBoatLiveDataOptions {
  /**
   * Called when cloud pushes a `user.<id>.registry` event — i.e. the
   * user's boat list has changed (claim / delete / deregister). The
   * caller should re-fetch /v1/boats and update their boatIds. The
   * hook will then automatically (un)subscribe to the new set.
   *
   * Task p2_boat_unlink_cloud plumbs this via the user-WS so the dashboard updates
   * without a manual refresh after relink.
   */
  onRegistry?: (event: { kind: string; boatId?: string }) => void;
}

export function useBoatLiveData(
  boatIds: ReadonlyArray<string>,
  options: UseBoatLiveDataOptions = {}
): BoatLiveData {
  // Mutable refs hold the working maps; we replace them with fresh
  // instances on each update so React notices the change. This avoids
  // a deep diff per frame while keeping the API immutable to callers.
  // One store for the hook's whole lifetime. Its identity is threaded through
  // React context, so it must never change — a new store each render would
  // invalidate every memo downstream, which is the cost this lane exists to
  // avoid.
  const liveStoreRef = useRef<LiveSampleStore | null>(null);
  liveStoreRef.current ??= new LiveSampleStore();
  const liveStore = liveStoreRef.current;

  const [snapshot, setSnapshot] = useState<BoatLiveData>(() => ({
    status: 'connecting',
    latest: new Map(),
    lastSampleAt: new Map(),
    infoRevision: new Map(),
    anchorCommandStatus: new Map(),
    boardChanged: new Map(),
    equipmentRevision: new Map(),
    namesRevision: new Map(),
    looksChanged: new Map(),
    liveStore,
  }));

  const latestRef = useRef<Map<string, Map<string, LiveSample>>>(new Map());
  const lastSampleAtRef = useRef<Map<string, number>>(new Map());
  // Newest display time published per boat AND PATH — the monotonic floor each
  // path's next sample is clamped against, so each instrument keeps its own
  // timeline instead of being dragged along by the fastest one.
  const displayFloorsRef = useRef<Map<string, Map<string, number>>>(new Map());
  // Per boat: the recent frames' clock offsets, and the smallest of them, which
  // dates each sample (`sampledAt`, TOM-441).
  const clockWindowsRef = useRef<Map<string, ClockOffsetObservation[]>>(new Map());
  const clockOffsetRef = useRef<Map<string, number>>(new Map());
  const infoRevisionRef = useRef<Map<string, number>>(new Map());
  const anchorStatusRef = useRef<Map<string, AnchorCommandStatusEvent>>(new Map());
  const boardChangedRef = useRef<Map<string, BoardChangedEvent>>(new Map());
  const equipmentRevisionRef = useRef<Map<string, number>>(new Map());
  const namesRevisionRef = useRef<Map<string, number>>(new Map());
  const looksChangedRef = useRef<Map<string, LooksChangedEvent>>(new Map());
  // Ref-wrap the optional onRegistry callback so its identity changing
  // across renders doesn't re-open the WebSocket.
  const onRegistryRef = useRef(options.onRegistry);
  onRegistryRef.current = options.onRegistry;

  // Stringify+sort so the effect re-runs only when the set of boat ids
  // actually changes (not just the array identity).
  const idsKey = [...boatIds].sort().join(',');

  useEffect(() => {
    if (typeof WebSocket === 'undefined') return;
    const ids = idsKey === '' ? [] : idsKey.split(',');
    // The WS is opened even when there are no boats to subscribe to,
    // so registry events still reach the dashboard — a user who just
    // claimed their first boat will see the card appear without a
    // refresh.

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = BACKOFF_INITIAL_MS;
    let cancelled = false;
    let snapshotTimer: ReturnType<typeof setTimeout> | null = null;
    let lastSnapshotAt = 0;

    function publishSnapshot(status: ConnectionStatus): void {
      setSnapshot({
        status,
        latest: snapshotLatest(latestRef.current),
        lastSampleAt: new Map(lastSampleAtRef.current),
        infoRevision: new Map(infoRevisionRef.current),
        anchorCommandStatus: new Map(anchorStatusRef.current),
        boardChanged: new Map(boardChangedRef.current),
        equipmentRevision: new Map(equipmentRevisionRef.current),
        namesRevision: new Map(namesRevisionRef.current),
        looksChanged: new Map(looksChangedRef.current),
        liveStore,
      });
    }

    function scheduleReconnect(): void {
      if (cancelled) return;
      // Jitter, because a fleet does not reconnect one boat at a time. A boat
      // coming back into cell coverage brings every viewer's socket back at
      // once, and an unjittered doubling schedule lines them all up on the same
      // millisecond — the server sees a thundering herd on exactly the beat it
      // is least able to absorb one.
      const delay = backoffMs * (0.75 + Math.random() * 0.5);
      backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
      publishSnapshot('closed');
      reconnectTimer = setTimeout(connect, delay);
    }

    function handleMessage(ev: MessageEvent): void {
      if (typeof ev.data !== 'string') return;
      let frame: IncomingFrame;
      try {
        frame = JSON.parse(ev.data) as IncomingFrame;
      } catch {
        return;
      }
      if (frame.type === 'registry') {
        // Cloud pushed a boat-list-changed event for the authenticated
        // user (task p2_boat_unlink_cloud). Forward to the caller so they can re-fetch
        // /v1/boats — the next render will pass an updated boatIds
        // list and the effect will (un)subscribe accordingly.
        const cb = onRegistryRef.current;
        if (cb) {
          const payload = (frame.payload ?? {}) as { kind?: unknown; boatId?: unknown };
          const event: { kind: string; boatId?: string } = {
            kind: typeof payload.kind === 'string' ? payload.kind : 'changed',
          };
          if (typeof payload.boatId === 'string') event.boatId = payload.boatId;
          cb(event);
        }
        return;
      }
      if (frame.type === 'looks') {
        // The owner's own looks for one of their boats changed (TOM-592): the
        // frame names only the boat and the revision, on their own topic.
        const looks = parseLooksChangedFrame(frame);
        if (looks !== null) {
          const seq = (looksChangedRef.current.get(looks.boatId)?.seq ?? 0) + 1;
          looksChangedRef.current.set(looks.boatId, { revision: looks.revision, seq });
          publishSnapshot('open');
        }
        return;
      }
      if (frame.type === 'info' && typeof frame.topic === 'string') {
        const match = /^boat\.([0-9a-f-]{36})\.info$/i.exec(frame.topic);
        const boatId = match?.[1];
        if (boatId === undefined) return;
        infoRevisionRef.current.set(boatId, (infoRevisionRef.current.get(boatId) ?? 0) + 1);
        const changed = parseBoardChangedFrame(frame.payload);
        if (changed !== null) {
          const seq = (boardChangedRef.current.get(boatId)?.seq ?? 0) + 1;
          boardChangedRef.current.set(boatId, { ...changed, seq });
        }
        if (isNamesChangedFrame(frame.payload)) {
          window.dispatchEvent(new CustomEvent(BOAT_NAMES_CHANGED_EVENT, { detail: { boatId } }));
          namesRevisionRef.current.set(boatId, (namesRevisionRef.current.get(boatId) ?? 0) + 1);
          equipmentRevisionRef.current.set(
            boatId,
            (equipmentRevisionRef.current.get(boatId) ?? 0) + 1
          );
        }
        if (isEquipmentChangedFrame(frame.payload)) {
          equipmentRevisionRef.current.set(
            boatId,
            (equipmentRevisionRef.current.get(boatId) ?? 0) + 1
          );
        }
        publishSnapshot('open');
        return;
      }
      if (frame.type === 'command_ack' && typeof frame.topic === 'string') {
        const match = /^boat\.([0-9a-f-]{36})\.cmdAck$/i.exec(frame.topic);
        const boatId = match?.[1];
        const payload = frame.payload as
          | {
              kind?: unknown;
              commandKey?: unknown;
              action?: unknown;
              phase?: unknown;
              detail?: unknown;
            }
          | undefined;
        if (
          boatId !== undefined &&
          payload !== undefined &&
          payload.kind === 'anchor_command_status' &&
          typeof payload.commandKey === 'string' &&
          isAnchorAction(payload.action) &&
          (payload.phase === 'accepted' ||
            payload.phase === 'complete' ||
            payload.phase === 'rejected')
        ) {
          anchorStatusRef.current.set(boatId, {
            commandKey: payload.commandKey,
            action: payload.action,
            phase: payload.phase,
            ...(typeof payload.detail === 'string' ? { detail: payload.detail } : {}),
            receivedAt: Date.now(),
          });
          publishSnapshot('open');
        }
        return;
      }
      if (frame.type !== 'data' || typeof frame.topic !== 'string') return;
      const boatId = extractBoatId(frame.topic);
      if (boatId === null) return;
      if (!Array.isArray(frame.payload)) return;

      const now = Date.now();
      let perBoat = latestRef.current.get(boatId);
      if (perBoat === undefined) {
        perBoat = new Map();
        latestRef.current.set(boatId, perBoat);
      }
      const parsedAll: LiveSample[] = [];
      const parsedFrame: LiveSample[] = [];
      for (const rawSample of frame.payload) {
        const parsed = parseSample(rawSample, now);
        if (parsed !== null) parsedFrame.push(parsed);
      }
      // By each sample's own time, not by arrival: a stored window landing after
      // the live lane's fresher sample of a path must not set it back, in this
      // lane or in lane 1 below, which takes only the samples kept here
      // (TOM-447).
      parsedAll.push(
        ...foldFrame(perBoat, parsedFrame, now, clockOffsetRef.current.get(boatId) ?? 0)
      );
      if (parsedAll.length === 0) return;

      // Re-time the frame's boat-clock stamps onto this browser's clock,
      // preserving their spacing (see motion.ts `retimeBatch` for why this is
      // spacing-only and not clock-offset estimation). A fast-lane frame
      // carries several samples ~200 ms apart; the interpolator needs those
      // gaps, and the boat's absolute clock is not trustworthy.
      const boatTimes = parsedAll.map((s) => Date.parse(s.time));
      const usable = boatTimes.every((t) => Number.isFinite(t));
      const retimed = usable
        ? retimeBatch(boatTimes, now)
        : // Unparseable times (shouldn't happen; the cloud shape-checks them)
          // — fall back to arrival time for the whole frame.
          parsedAll.map(() => now);
      // Date each sample by its own time on this clock (TOM-441), through the
      // boat's measured offset. An offset that moves re-dates what the boat
      // already sent: smaller, the fast lane's after a load that opened on a
      // batch; larger, a boat clock that stepped back (TOM-447).
      if (usable) {
        let window = clockWindowsRef.current.get(boatId);
        if (window === undefined) {
          window = [];
          clockWindowsRef.current.set(boatId, window);
        }
        const offset = observeClockOffset(window, now, now - Math.max(...boatTimes));
        const before = clockOffsetRef.current.get(boatId);
        clockOffsetRef.current.set(boatId, offset);
        parsedAll.forEach((s, i) => {
          s.sampledAt = sampledAt(boatTimes[i]!, offset, now);
        });
        if (before !== undefined && Math.abs(offset - before) > 1_000) {
          const fresh = new Set(parsedAll);
          for (const [path, s] of perBoat) {
            if (fresh.has(s)) continue;
            perBoat.set(path, {
              ...s,
              sampledAt: sampledAt(Date.parse(s.time), offset, s.receivedAt),
            });
          }
        }
      }
      // Monotonic clamp PER PATH, and never past arrival. Each instrument is
      // its own timeline — an anemometer and a compass stamp independently —
      // so a floor shared across the frame ratchets every path forward by the
      // spread of the fastest one, the timeline drifts into the future, and
      // the fixed playhead falls behind: numerals show the truth while the
      // needle replays old readings in slow motion. Measured on the bench
      // before this was per-path.
      let floors = displayFloorsRef.current.get(boatId);
      if (floors === undefined) {
        floors = new Map();
        displayFloorsRef.current.set(boatId, floors);
      }
      for (let i = 0; i < parsedAll.length; i++) {
        const sample = parsedAll[i]!;
        const at = nextDisplayAt(retimed[i] ?? now, floors.get(sample.path), now);
        floors.set(sample.path, at);
        sample.displayAt = at;
      }

      // Lane 1 — per-path, synchronous, unthrottled. This is what the
      // instruments subscribe to.
      liveStore.ingest(
        boatId,
        parsedAll.map((s) => ({
          path: s.path,
          value: s.value,
          at: s.displayAt ?? now,
          ...(s.unit !== undefined ? { unit: s.unit } : {}),
          ...(s.source !== undefined ? { source: s.source } : {}),
          ...(s.deviceId !== undefined ? { deviceId: s.deviceId } : {}),
        }))
      );

      // Lane 2 — the cloned-Map snapshot, THROTTLED. Every consumer of
      // `latest` (Alarms, SignalPicker, alarmLive, overview) re-renders when
      // it changes, and that is the whole page — for views that are perfectly
      // happy at 1 Hz. Leading + trailing, so the
      // first sample still lands immediately and the last one is never lost.
      lastSampleAtRef.current.set(boatId, now);
      publishSnapshotThrottled();
    }

    /** Publish `latest` at most once per SNAPSHOT_MIN_INTERVAL_MS. */
    function publishSnapshotThrottled(): void {
      const now = Date.now();
      const since = now - lastSnapshotAt;
      if (since >= SNAPSHOT_MIN_INTERVAL_MS) {
        lastSnapshotAt = now;
        publishSnapshot('open');
        return;
      }
      if (snapshotTimer !== null) return;
      snapshotTimer = setTimeout(() => {
        snapshotTimer = null;
        lastSnapshotAt = Date.now();
        publishSnapshot('open');
      }, SNAPSHOT_MIN_INTERVAL_MS - since);
    }

    function connect(): void {
      if (cancelled) return;
      publishSnapshot('connecting');
      const url = new URL('/v1/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffMs = BACKOFF_INITIAL_MS;
        for (const id of ids) {
          try {
            ws.send(JSON.stringify({ type: 'subscribe', topic: dataTopicForBoat(id) }));
            ws.send(JSON.stringify({ type: 'subscribe', topic: infoTopicForBoat(id) }));
            namesRevisionRef.current.set(id, (namesRevisionRef.current.get(id) ?? 0) + 1);
            window.dispatchEvent(
              new CustomEvent(BOAT_NAMES_CHANGED_EVENT, { detail: { boatId: id } })
            );
            ws.send(JSON.stringify({ type: 'subscribe', topic: cmdAckTopicForBoat(id) }));
          } catch {
            // socket gone; onclose will trigger reconnect
          }
        }
        publishSnapshot('open');
      };
      ws.onmessage = handleMessage;
      ws.onclose = (ev) => {
        socket = null;
        // Renew before retrying when the cloud closed us for an expired access
        // token (task ws_session_expiry): reconnecting with the cookie it just
        // refused only earns another refusal, and the screen would sit stale
        // through the backoff. Reconnects either way.
        void beforeWsReconnect(ev.code).then(scheduleReconnect, scheduleReconnect);
      };
      ws.onerror = () => {
        // onerror fires before onclose; the close handler will reconnect.
        // Don't double-schedule here.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (snapshotTimer !== null) {
        clearTimeout(snapshotTimer);
        snapshotTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          /* noop */
        }
        socket = null;
      }
    };
    // `liveStore` is created once per hook instance and never replaced, so it
    // can never actually re-trigger this effect — listing it satisfies the
    // exhaustive-deps rule without reopening the socket.
  }, [idsKey, liveStore]);

  return snapshot;
}
